import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box, Button, Chip, Dialog, DialogContent, DialogTitle, Grid, IconButton, List, ListItemButton,
  ListItemText, Paper, Stack, Switch, TextField, Typography, alpha, useTheme, Tabs, Tab,
  ToggleButton, ToggleButtonGroup, Tooltip, TablePagination, Alert,
} from '@mui/material';
import { Add, Close, Delete, Edit, Search, TableRows, ViewSidebar, CardMembership } from '@mui/icons-material';
import ZonePartnerSubscriptionsDrawer from '../../components/zones/ZonePartnerSubscriptionsDrawer';
import { zoneSupportsPartnerSubscription } from '../../utils/partnerZoneUtils';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import NonNegativeNumberField from '../../components/ui/forms/NonNegativeNumberField';
import ZoneRecordTable from '../../components/zones/ZoneRecordTable';
import ZonePolygonMap from '../../components/maps/ZonePolygonMap';
import MapErrorBoundary from '../../components/maps/MapErrorBoundary';
import ZoneFarePreview from './components/ZoneFarePreview';
import { formatSurgeTime12h } from './components/ZoneSurgeStep';
import { buildServicePricingRow, formatZoneDisplayName, formatZoneLevelLabel, zoneManagementPath } from './zoneTypes';
import {
  listZones, getZone, toggleZone, deleteZone,
  listOutOfZoneTiers, createOutOfZoneTier, deleteOutOfZoneTier,
  type OperationalZoneRow, type ZoneDetail,
} from '../../services/zoneService';
import type { ServiceHubType } from '../../constants/serviceHubs';
import { selectIsCityAdmin, selectIsSuperAdmin, selectUser } from '../../store/slices/authSlice';
import { chargeFieldLabel, formatMoney } from '../../utils/currency';

type ZoneViewMode = 'table' | 'detail';

function isDeliveryServicePricingRow(p: ZoneDetail['servicePricing'][number]) {
  const hubType = typeof p.serviceId === 'object' ? p.serviceId.hubType : '';
  const slug = typeof p.serviceId === 'object' ? (p.serviceId.slug || '') : '';
  if (hubType === 'delivery') return true;
  return ['food-delivery', 'grocery-delivery', 'porter-service'].includes(slug);
}

function buildServicePricingPreview(zoneDetail: ZoneDetail | undefined, deliveryOnly = false) {
  if (!zoneDetail?.servicePricing.length) return [];
  const map = new Map<string, ReturnType<typeof buildServicePricingRow>>();
  for (const p of zoneDetail.servicePricing) {
    if (deliveryOnly && !isDeliveryServicePricingRow(p)) continue;
    if (deliveryOnly && (p as { vehicleTypeCode?: string }).vehicleTypeCode) continue;
    const sid = typeof p.serviceId === 'object' ? p.serviceId._id : String(p.serviceId);
    const name = typeof p.serviceId === 'object' ? p.serviceId.name : sid;
    const slug = typeof p.serviceId === 'object' ? p.serviceId.slug : '';
    const hubType = (typeof p.serviceId === 'object' ? p.serviceId.hubType : 'transport') as ServiceHubType;
    if (!map.has(sid)) {
      map.set(sid, buildServicePricingRow({ _id: sid, name, slug, hubType }));
    }
    const row = map.get(sid)!;
    const pricing = {
      baseFare: p.baseFare,
      baseDistanceKm: p.baseDistanceKm,
      perKmRate: p.perKmRate,
      perMinRate: p.perMinRate,
      deliveryCharge: p.deliveryCharge,
      minimumFare: p.minimumFare,
    };
    if (p.scheduleType === 'ride_later') row.rideLater = { ...row.rideLater, ...pricing };
    else row.rideNow = { ...row.rideNow, ...pricing };
  }
  return Array.from(map.values());
}

function ZoneDetailBody({
  zone,
  zoneDetail,
  mapZones,
  detailTab,
  onDetailTabChange,
  outTiers,
  onRefetchTiers,
  tierKm,
  tierPrice,
  onTierKmChange,
  onTierPriceChange,
  onAddTier,
  headerExtra,
  deliveryScoped = false,
}: {
  zone: OperationalZoneRow;
  zoneDetail?: ZoneDetail;
  mapZones: Array<{ id: string; name: string; polygon: { lat: number; lng: number }[]; color?: string; selected?: boolean }>;
  detailTab: number;
  onDetailTabChange: (tab: number) => void;
  outTiers: Array<{ _id: string; kilometer: number; price: number }>;
  onRefetchTiers: () => void;
  tierKm: string;
  tierPrice: string;
  onTierKmChange: (v: string) => void;
  onTierPriceChange: (v: string) => void;
  onAddTier: () => void;
  headerExtra?: React.ReactNode;
  deliveryScoped?: boolean;
}) {
  const theme = useTheme();
  const pricingRows = useMemo(() => {
    const rows = zoneDetail?.servicePricing || [];
    if (!deliveryScoped) return rows;
    return rows.filter((p) => isDeliveryServicePricingRow(p) && !(p as { vehicleTypeCode?: string }).vehicleTypeCode);
  }, [zoneDetail, deliveryScoped]);
  const servicePricingForPreview = useMemo(
    () => buildServicePricingPreview(zoneDetail, deliveryScoped),
    [zoneDetail, deliveryScoped],
  );

  return (
    <Stack spacing={2}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h6" fontWeight={700}>{formatZoneDisplayName(zone.name)}</Typography>
          <Stack direction="row" gap={0.5} mt={0.5} flexWrap="wrap">
            <Chip size="small" label={zone.code} />
            <Chip size="small" label={formatZoneLevelLabel(zone.zoneLevel)} variant="outlined" />
            {zone.country && <Chip size="small" variant="outlined" label={zone.country} />}
            {!deliveryScoped && zone.biddingEnabled && <Chip size="small" color="primary" label="Bidding" />}
            <Chip size="small" color={zone.isActive ? 'success' : 'default'} label={zone.isActive ? 'Active' : 'Inactive'} />
          </Stack>
        </Box>
        {headerExtra}
      </Box>

      <MapErrorBoundary fallbackTitle="Zone map could not load">
        <ZonePolygonMap
          zones={mapZones}
          polygon={zone.polygon}
          height={240}
          center={zone.centerLat != null && zone.centerLng != null
            ? { lat: zone.centerLat, lng: zone.centerLng }
            : undefined}
        />
      </MapErrorBoundary>

      <Tabs value={detailTab} onChange={(_, v) => onDetailTabChange(v)}>
        <Tab label="Pricing" />
        <Tab label="Surge" />
        <Tab label="Fare calculator" />
        <Tab label="Out of zone" />
      </Tabs>

      {detailTab === 0 && (
        <Paper variant="outlined" sx={{ p: 2, maxHeight: 280, overflow: 'auto' }}>
          {pricingRows.map((p) => (
            <Box key={p._id} sx={{ mb: 1, p: 1, borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
              <Typography variant="body2" fontWeight={600}>
                {typeof p.serviceId === 'object' ? p.serviceId.name : 'Service'}
                {!deliveryScoped && (p as { vehicleTypeCode?: string }).vehicleTypeCode
                  ? ` · ${(p as { vehicleTypeCode?: string }).vehicleTypeCode}`
                  : ''} · {p.scheduleType}
              </Typography>
              <Stack direction="row" gap={0.5} flexWrap="wrap" mt={0.5}>
                <Chip size="small" label={`Base ${formatMoney(p.baseFare, zone.currency)}`} />
                <Chip size="small" variant="outlined" label={`${p.baseDistanceKm}km free`} />
                <Chip size="small" variant="outlined" label={`${formatMoney(p.perKmRate, zone.currency)}/km`} />
                {p.deliveryCharge ? <Chip size="small" variant="outlined" label={`Del ${formatMoney(p.deliveryCharge, zone.currency)}`} /> : null}
              </Stack>
            </Box>
          ))}
          {!pricingRows.length && (
            <Typography variant="body2" color="text.secondary">
              {deliveryScoped ? 'No delivery pricing configured for this zone yet.' : 'No pricing configured'}
            </Typography>
          )}
        </Paper>
      )}

      {detailTab === 1 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          {(zoneDetail?.surgePricing || []).map((s) => (
            <Chip key={s._id} sx={{ mr: 0.5, mb: 0.5 }} color="warning" size="small"
              label={`${s.name}: ${s.multiplier}x (${formatSurgeTime12h(s.startHour)}–${formatSurgeTime12h(s.endHour)})`} />
          ))}
          {!zoneDetail?.surgePricing?.length && <Typography variant="body2" color="text.secondary">No surge rules</Typography>}
        </Paper>
      )}

      {detailTab === 2 && (
        <ZoneFarePreview servicePricing={servicePricingForPreview} scheduleType="ride_now" currency={zone.currency} />
      )}

      {detailTab === 3 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>Out-of-zone distance tiers</Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.5} mb={2}>
            {outTiers.map((t) => (
              <Chip key={t._id} size="small" variant="outlined" label={`≥${t.kilometer}km → ${formatMoney(t.price, zone.currency)}`}
                onDelete={() => deleteOutOfZoneTier(t._id).then(() => onRefetchTiers())} />
            ))}
          </Stack>
          <Stack direction="row" gap={1}>
            <NonNegativeNumberField size="small" integer label="Km" value={tierKm} commitEmptyAsZero={false}
              onValueChange={(n) => onTierKmChange(String(n))} onEmpty={() => onTierKmChange('')} sx={{ width: 80 }} />
            <NonNegativeNumberField size="small" label={chargeFieldLabel('Price', zone.currency)} value={tierPrice} commitEmptyAsZero={false}
              onValueChange={(n) => onTierPriceChange(String(n))} onEmpty={() => onTierPriceChange('')} sx={{ width: 100 }} />
            <Button size="small" variant="outlined" onClick={onAddTier}>Add tier</Button>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}

export default function ZoneManagementPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scopeHub = (searchParams.get('hub') || '') as ServiceHubType | '';
  const scopeService = (searchParams.get('service') || '').trim();
  const deliveryScoped = scopeHub === 'delivery';
  const zonesNewPath = zoneManagementPath({
    hub: scopeHub || undefined,
    service: scopeService || undefined,
    action: 'new',
  });
  const qc = useQueryClient();
  const user = useSelector(selectUser);
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const isCityAdmin = useSelector(selectIsCityAdmin);
  const canManageZones = isSuperAdmin || user?.userType === 'client_admin';
  const [viewMode, setViewMode] = useState<ZoneViewMode>('table');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [viewZone, setViewZone] = useState<OperationalZoneRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<OperationalZoneRow | null>(null);
  const [tierKm, setTierKm] = useState('');
  const [tierPrice, setTierPrice] = useState('');
  const [detailTab, setDetailTab] = useState(0);
  const [viewDetailTab, setViewDetailTab] = useState(0);
  const [subscriptionZone, setSubscriptionZone] = useState<OperationalZoneRow | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const { data: zonesRes, isLoading } = useQuery({
    queryKey: ['operational-zones', search, page, pageSize],
    queryFn: () => listZones({ search: search.trim() || undefined, page: page + 1, limit: pageSize }),
  });

  const zones = zonesRes?.rows || [];
  const totalCount = Number(zonesRes?.meta?.total ?? zones.length);
  const filtered = zones;

  const selected = zones.find((z) => z._id === selectedId) || filtered[0] || null;

  const { data: zoneDetail } = useQuery({
    queryKey: ['zone-detail', selected?._id],
    queryFn: () => getZone(selected!._id),
    enabled: !!selected?._id && viewMode === 'detail',
  });

  const { data: viewZoneDetail } = useQuery({
    queryKey: ['zone-detail', viewZone?._id],
    queryFn: () => getZone(viewZone!._id),
    enabled: !!viewZone?._id,
  });

  const { data: outTiers = [], refetch: refetchTiers } = useQuery({
    queryKey: ['out-of-zone-tiers'],
    queryFn: listOutOfZoneTiers,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['operational-zones'] });
    qc.invalidateQueries({ queryKey: ['zone-detail'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => toggleZone(id, isActive),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteZone,
    onSuccess: () => { invalidate(); setDeleteTarget(null); setViewZone(null); },
  });

  const openViewDialog = useCallback((zone: OperationalZoneRow) => {
    setViewZone(zone);
    setViewDetailTab(0);
  }, []);

  const mapZonesFor = (focusId: string) => zones.map((z) => ({
    id: z._id,
    name: formatZoneDisplayName(z.name),
    polygon: z.polygon || [],
    color: z.color,
    selected: z._id === focusId,
  }));

  const addTier = async () => {
    await createOutOfZoneTier(Number(tierKm), Number(tierPrice));
    setTierKm('');
    setTierPrice('');
    refetchTiers();
  };

  return (
    <PageShell>
      <PageHeader
        title="Zone Management"
        subtitle={
          deliveryScoped
            ? (scopeService === 'food-delivery'
              ? 'Delivery · Food — zone polygons and Partner Pricing (no transport services in this flow)'
              : scopeService === 'grocery-delivery'
                ? 'Delivery · Grocery — zone polygons and Partner Pricing (no transport services in this flow)'
                : 'Delivery hub — zone polygons and Partner Pricing for delivery services only')
            : isCityAdmin
              ? 'View operating zones assigned to you. Zone creation and pricing changes are managed by tenant admin.'
              : 'Zone-wise pricing for all services — Ride Now, Ride Later, Surge & per-vehicle transport rates'
        }
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <ToggleButtonGroup
              size="small"
              exclusive
              value={viewMode}
              onChange={(_, v: ZoneViewMode | null) => v && setViewMode(v)}
            >
              <ToggleButton value="table">
                <TableRows fontSize="small" sx={{ mr: 0.5 }} /> Table
              </ToggleButton>
              <ToggleButton value="detail">
                <ViewSidebar fontSize="small" sx={{ mr: 0.5 }} /> Detail
              </ToggleButton>
            </ToggleButtonGroup>
            {canManageZones && (
              <Button variant="contained" startIcon={<Add />} onClick={() => navigate(zonesNewPath)}>Add Zone</Button>
            )}
          </Stack>
        }
      />

      {deliveryScoped && (
        <Alert
          severity="info"
          sx={{
            mb: 2,
            bgcolor: (t) => (t.palette.mode === 'light' ? alpha(t.palette.info.main, 0.12) : alpha(t.palette.info.main, 0.22)),
            border: 1,
            borderColor: 'info.main',
            color: 'text.primary',
            '& .MuiAlert-icon': { color: 'info.dark' },
            '& .MuiAlert-message': { color: 'text.primary', fontWeight: 600, lineHeight: 1.55 },
          }}
        >
          Opened from Delivery Food / Delivery hub — create and edit flows only offer Delivery services
          (transport options are hidden). Zone list still shows all operational zones so you can enable coverage.
        </Alert>
      )}

      {viewMode === 'table' ? (
        <Stack spacing={1.5}>
          <TextField
            size="small"
            placeholder="Search zones by name or code…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
            sx={{ maxWidth: 360 }}
          />
          <ZoneRecordTable
            rows={filtered}
            loading={isLoading}
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
            onView={openViewDialog}
            onEdit={canManageZones ? (row) => navigate(zoneManagementPath({
              hub: scopeHub || undefined,
              service: scopeService || undefined,
              zoneId: row._id,
              action: 'edit',
            })) : undefined}
            onDelete={canManageZones ? setDeleteTarget : undefined}
            onToggleActive={canManageZones ? (row, isActive) => toggleMutation.mutate({ id: row._id, isActive }) : undefined}
            onManageSubscriptions={setSubscriptionZone}
            allowMutations={canManageZones}
          />
        </Stack>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth size="small" placeholder="Search zones..." value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
              sx={{ mb: 1 }}
            />
            <Paper variant="outlined" sx={{ maxHeight: 520, overflow: 'auto' }}>
              <List dense>
                {filtered.map((z, i) => (
                  <ListItemButton key={z._id} selected={selected?._id === z._id} onClick={() => setSelectedId(z._id)}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ minWidth: 36, mr: 1 }}>{page * pageSize + i + 1}</Typography>
                    <ListItemText
                      primary={formatZoneDisplayName(z.name)}
                      secondary={`${z.code} · ${formatZoneLevelLabel(z.zoneLevel)}`}
                      primaryTypographyProps={{ fontWeight: 600, noWrap: true }}
                    />
                    {canManageZones ? (
                      <Switch size="small" checked={z.isActive} onClick={(e) => e.stopPropagation()}
                        onChange={(e) => toggleMutation.mutate({ id: z._id, isActive: e.target.checked })} />
                    ) : (
                      <Chip size="small" label={z.isActive ? 'Active' : 'Off'} variant="outlined" />
                    )}
                  </ListItemButton>
                ))}
                {!isLoading && !filtered.length && (
                  <Box p={2}><Typography variant="body2" color="text.secondary">No zones</Typography></Box>
                )}
              </List>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_, next) => setPage(next)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{ borderTop: 1, borderColor: 'divider' }}
              />
            </Paper>
          </Grid>

          <Grid item xs={12} md={9}>
            {selected ? (
              <ZoneDetailBody
                zone={selected}
                zoneDetail={zoneDetail}
                mapZones={mapZonesFor(selected._id)}
                detailTab={detailTab}
                onDetailTabChange={setDetailTab}
                outTiers={outTiers}
                onRefetchTiers={refetchTiers}
                tierKm={tierKm}
                tierPrice={tierPrice}
                onTierKmChange={setTierKm}
                onTierPriceChange={setTierPrice}
                onAddTier={addTier}
                deliveryScoped={deliveryScoped}
                headerExtra={canManageZones ? (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    {zoneSupportsPartnerSubscription(selected.metadata as Record<string, unknown> | undefined) && (
                      <Tooltip title="Partner subscription plans">
                        <IconButton
                          color="primary"
                          onClick={() => setSubscriptionZone(selected)}
                          sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.08) }}
                        >
                          <CardMembership />
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton onClick={() => navigate(zoneManagementPath({
                      hub: scopeHub || undefined,
                      service: scopeService || undefined,
                      zoneId: selected._id,
                      action: 'edit',
                    }))}><Edit /></IconButton>
                    <IconButton color="error" onClick={() => setDeleteTarget(selected)}><Delete /></IconButton>
                  </Stack>
                ) : undefined}
              />
            ) : (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {canManageZones ? 'Select a zone or add a new one' : 'Select a zone to view details'}
                </Typography>
                {canManageZones && (
                  <Button sx={{ mt: 2 }} variant="contained" startIcon={<Add />} onClick={() => navigate(zonesNewPath)}>Add Zone</Button>
                )}
              </Paper>
            )}
          </Grid>
        </Grid>
      )}

      <Dialog open={!!viewZone} onClose={() => setViewZone(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          Zone details
          <IconButton onClick={() => setViewZone(null)} sx={{ position: 'absolute', right: 12, top: 12 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {viewZone && (
            <ZoneDetailBody
              zone={viewZone}
              zoneDetail={viewZoneDetail}
              mapZones={mapZonesFor(viewZone._id)}
              detailTab={viewDetailTab}
              onDetailTabChange={setViewDetailTab}
              outTiers={outTiers}
              onRefetchTiers={refetchTiers}
              tierKm={tierKm}
              tierPrice={tierPrice}
              onTierKmChange={setTierKm}
              onTierPriceChange={setTierPrice}
              onAddTier={addTier}
              deliveryScoped={deliveryScoped}
              headerExtra={(
                <Stack direction="row" alignItems="center" spacing={1}>
                  {zoneSupportsPartnerSubscription(viewZone.metadata as Record<string, unknown> | undefined) && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<CardMembership />}
                      onClick={() => setSubscriptionZone(viewZone)}
                    >
                      Subscriptions
                    </Button>
                  )}
                  <Button size="small" variant="outlined" startIcon={<Edit />} onClick={() => navigate(zoneManagementPath({
                    hub: scopeHub || undefined,
                    service: scopeService || undefined,
                    zoneId: viewZone._id,
                    action: 'edit',
                  }))}>
                    Edit
                  </Button>
                  <IconButton color="error" onClick={() => setDeleteTarget(viewZone)}><Delete /></IconButton>
                </Stack>
              )}
            />
          )}
        </DialogContent>
      </Dialog>

      <ZonePartnerSubscriptionsDrawer
        open={!!subscriptionZone}
        zone={subscriptionZone}
        onClose={() => setSubscriptionZone(null)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete zone?"
        message={
          deleteMutation.isError
            ? ((deleteMutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message
              || 'Failed to delete zone. It may still be in use.')
            : `Delete ${deleteTarget?.name}? All zone pricing will be removed.`
        }
        loading={deleteMutation.isPending}
        onCancel={() => { deleteMutation.reset(); setDeleteTarget(null); }}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget._id)}
      />
    </PageShell>
  );
}
